import React from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router-dom'

const SCROLL_RESTORATION_TIMEOUT_MS = 3000
const TRY_TO_SCROLL_INTERVAL_MS = 50

// from https://github.com/faisalman/ua-parser-js/blob/a88934fadf2393f3757f1e8b9966d8c0244120b1/src/ua-parser.js#L255
const POLYFILL_BROWSERS_REGEXP = [
  /(edge|edgios|edga|edg)\/((\d+)?[\w.]+)/i, // Edge
  /(trident).+rv[:\s]([\w.]+).+like\sgecko/i, // IE11
]

class RestoreScroll extends React.PureComponent {
  timeoutHandle = null

  scrollBarWidth = null

  componentDidMount() {
    const ua = (
      (navigator && typeof navigator.userAgent === 'string' && navigator.userAgent) ||
      ''
    ).substr(0, 150)
    const enable = POLYFILL_BROWSERS_REGEXP.some((regexp) => new RegExp(regexp).test(ua))
    if (!enable) {
      return
    }
    if (window.history.pushState) {
      const originalPushState = window.history.pushState
      const originalReplaceState = window.history.replaceState

      // Store current scroll position in current state when navigating away.
      window.history.pushState = (...args) => {
        const newStateOfCurrentPage = {
          ...window.history.state,
          __scrollX: window.pageXOffset || document.documentElement.scrollLeft,
          __scrollY: window.pageYOffset || document.documentElement.scrollTop,
        }
        originalReplaceState.call(window.history, newStateOfCurrentPage, '')

        originalPushState.apply(window.history, args)
      }

      // Make sure we don't throw away scroll position when calling "replaceState".
      window.history.replaceState = (state, ...otherArgs) => {
        const newState = {
          __scrollX: window.history.state && window.history.state.__scrollX,
          __scrollY: window.history.state && window.history.state.__scrollY,
          ...state,
        }

        originalReplaceState.apply(window.history, [newState].concat(otherArgs))
      }

      window.addEventListener('popstate', this.onPopState, true)
    }
  }

  // replaces old functionality for scrolling to top.
  componentDidUpdate(prevProps) {
    const {
      location: { search, pathname, state: { dontScroll } = {} },
    } = this.props

    const {
      history: { action },
      location: { search: prevSearch, pathname: prevPathname },
    } = prevProps

    if (search === prevSearch && pathname === prevPathname) {
      return
    }

    if (dontScroll) {
      return
    }

    if (['PUSH', 'REPLACE'].includes(action)) {
      // will trigger after new page load on PUSH or REPLACE and thus not interfere with polyfill for POP
      this.scrollToTop()
    }
  }

  componentWillUnmount() {
    window.removeEventListener('popstate', this.onPopState, true)
  }

  scrollToTop = () => {
    window.scrollTo(0, 0)
  }

  // Calculating width of browser's scrollbar
  getScrollbarWidth = () => {
    const outer = document.createElement('div')
    outer.style.visibility = 'hidden'
    outer.style.width = '100px'
    outer.style.msOverflowStyle = 'scrollbar'

    document.body.appendChild(outer)

    const widthNoScroll = outer.offsetWidth
    // force scrollbars
    outer.style.overflow = 'scroll'

    // add innerdiv
    const inner = document.createElement('div')
    inner.style.width = '100%'
    outer.appendChild(inner)

    const widthWithScroll = inner.offsetWidth

    // remove divs
    outer.parentNode.removeChild(outer)

    return widthNoScroll - widthWithScroll
  }

  // Try to scroll to the scrollTarget, but only if we can actually scroll
  // there. Otherwise keep trying until we time out, then scroll as far as
  // we can.
  tryToScrollTo = (scrollTarget) => {
    // Stop any previous calls to "tryToScrollTo".
    clearTimeout(this.timeoutHandle)

    const { body } = document
    const html = document.documentElement
    if (!this.scrollBarWidth) {
      this.scrollBarWidth = this.getScrollbarWidth()
    }

    // From http://stackoverflow.com/a/1147768
    const documentWidth = Math.max(
      body.scrollWidth,
      body.offsetWidth,
      html.clientWidth,
      html.scrollWidth,
      html.offsetWidth,
    )
    const documentHeight = Math.max(
      body.scrollHeight,
      body.offsetHeight,
      html.clientHeight,
      html.scrollHeight,
      html.offsetHeight,
    )

    if (
      (documentWidth + this.scrollBarWidth - window.innerWidth >= scrollTarget.x &&
        documentHeight + this.scrollBarWidth - window.innerHeight >= scrollTarget.y) ||
      Date.now() > scrollTarget.latestTimeToTry
    ) {
      window.scrollTo(scrollTarget.x, scrollTarget.y)
    } else {
      this.timeoutHandle = setTimeout(
        () => this.tryToScrollTo(scrollTarget),
        TRY_TO_SCROLL_INTERVAL_MS,
      )
    }
  }

  // Try scrolling to the previous scroll position on popstate
  onPopState = () => {
    const { state } = window.history

    // eslint-disable-next-line no-restricted-globals
    if (state && isFinite(state.__scrollX) && isFinite(state.__scrollY)) {
      setTimeout(() =>
        this.tryToScrollTo({
          x: state.__scrollX,
          y: state.__scrollY,
          latestTimeToTry: Date.now() + SCROLL_RESTORATION_TIMEOUT_MS,
        }),
      )
    }
  }

  render() {
    return null
  }
}

RestoreScroll.propTypes = {
  location: PropTypes.shape({
    search: PropTypes.string,
    pathname: PropTypes.string,
    state: PropTypes.shape({ dontScroll: PropTypes.bool }),
  }),
  history: PropTypes.shape({ action: PropTypes.string }),
}

export default withRouter(RestoreScroll)
